import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../AdminLayout.tsx';
import { adminFetch, getDatabaseCollections, exportDatabaseCollection } from '../../../utils/adminFetch.ts';
import { formatBytes } from '../../../utils/formatBytes.ts';
import { useAdminAuth } from '../../../context/AdminAuthContext.tsx';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.athenex.xyz";
const ADMIN_API_PATH = "/admin";

interface Collection {
  name: string;
  size: number;
  count: number;
}

const DatabaseExport: React.FC = () => {
  const { getAdminAuthHeaders } = useAdminAuth();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);

  useEffect(() => {
    const fetchCollections = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const collectionsData = await getDatabaseCollections();
        const statsData = await adminFetch('/database/stats');
        const collectionStats = statsData.collection_stats || {};
        
        const collectionsWithStats = Object.keys(collectionsData.collections || {}).map(name => ({
          name,
          count: collectionStats[name]?.count || 0,
          size: collectionStats[name]?.size || 0,
        }));
        
        setCollections(collectionsWithStats);
        if (collectionsWithStats.length > 0) {
          setSelectedCollection(collectionsWithStats[0].name);
        }
      } catch (err: any) {
        console.error('Error fetching collections:', err.message);
        setError(err.message || "Failed to load database collections");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCollections();
  }, []);

  const handleExportCollection = async () => {
    if (!selectedCollection) return;
    
    setIsExporting(true);
    setError(null);
    setExportSuccess(null);
    
    try {
      const exportData = await exportDatabaseCollection(selectedCollection);
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedCollection}_export.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setExportSuccess(`Successfully exported ${selectedCollection} collection`);
    } catch (err: any) {
      console.error('Error exporting collection:', err.message);
      setError(err.message || `Failed to export ${selectedCollection} collection`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportFullDatabase = async () => {
    if (!window.confirm('This may take a while for large databases. Do you want to continue?')) {
      return;
    }
    
    setIsExporting(true);
    setError(null);
    setExportSuccess(null);
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}${ADMIN_API_PATH}/database/export-all`,
        {
          headers: getAdminAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const date = new Date();
      const timestamp = date.toISOString().replace(/[:.]/g, '-');
      
      link.setAttribute('download', `athenex_full_export_${timestamp}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      
      setExportSuccess('Successfully exported full database');
    } catch (err: any) {
      console.error('Error exporting full database:', err);
      setError('Failed to export full database');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <AdminLayout title="Database Export">
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <div className="space-y-8">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              <p>{error}</p>
            </div>
          )}
          
          {exportSuccess && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
              <p>{exportSuccess}</p>
            </div>
          )}
          
          <div className="bg-base rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold font-space-grotesk mb-4">Export Collection</h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="collectionSelect" className="block text-sm font-medium text-gray-dark mb-2">
                  Select Collection
                </label>
                <select
                  id="collectionSelect"
                  value={selectedCollection}
                  onChange={(e) => setSelectedCollection(e.target.value)}
                  className="w-full md:w-1/2 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-upload-button-pressed"
                >
                  {collections.map((collection) => (
                    <option key={collection.name} value={collection.name}>
                      {collection.name} ({collection.count} items, {formatBytes(collection.size)})
                    </option>
                  ))}
                </select>
              </div>
              
              <button
                onClick={handleExportCollection}
                disabled={isExporting || !selectedCollection}
                className="px-4 py-2 bg-upload-button-pressed text-white rounded-lg hover:bg-upload-button-hover hover:text-black disabled:opacity-50 flex items-center"
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <span className="material-icons mr-2">download</span>
                    Export Collection
                  </>
                )}
              </button>
            </div>
          </div>
          
          <div className="bg-base rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold font-space-grotesk mb-4">Export Full Database</h2>
            <p className="text-gray-dark mb-4">
              This will export all collections in the database. The process may take a while for large databases.
            </p>
            
            <button
              onClick={handleExportFullDatabase}
              disabled={isExporting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Exporting Database...
                </>
              ) : (
                <>
                  <span className="material-icons mr-2">cloud_download</span>
                  Export Full Database
                </>
              )}
            </button>
          </div>
          
          <div className="bg-base rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 bg-gray-base">
              <h3 className="text-lg font-bold">Available Collections</h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-base">
                <thead className="bg-gray-base">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                      Collection Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                      Documents Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-base divide-y divide-gray-base">
                  {collections.map((collection) => (
                    <tr key={collection.name} className="hover:bg-gray-light">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {collection.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {collection.count.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {formatBytes(collection.size)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => {
                            setSelectedCollection(collection.name);
                            handleExportCollection();
                          }}
                          disabled={isExporting}
                          className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                        >
                          Export
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default DatabaseExport;
